/**
 * useModifications — Firestore listener and mutations for a vehicle's
 * modifications subcollection.
 *
 * Modifications live at `vehicles/{vehicleId}/modifications/{modId}`.
 * Client-direct Firestore (mutable owner diary, offline-capable).
 * Mirrors useTasks / useServiceEntries — same hook pattern, same
 * fsSetDoc/fsDoc/fsCollection helpers, same ISO-string timestamp convention.
 *
 * Capacitor readiness:
 *   - No direct firebase/* imports in component consumers.
 *   - ISO strings for all timestamps (no Timestamp/FieldValue).
 *   - createdBy set client-side from auth.currentUser.uid.
 */

import { auth } from '@/lib/firebase';
import { track } from '@/lib/telemetry';
import type { ConcoursImpact, JudgeVisibility, Modification } from '@/types/domain';
import { useCallback, useState } from 'react';
import { useFirestoreCollection, type FirestoreConstraintsBuilder } from './useFirestoreCollection';
import { fsCollection, fsDoc, fsSetDoc } from './useFirestoreMutation';

// ---------------------------------------------------------------------------
// Read — onSnapshot list
// ---------------------------------------------------------------------------

export function useModifications(vehicleId: string | null | undefined) {
  const buildConstraints = useCallback<FirestoreConstraintsBuilder>(
    (sdk) => [sdk.orderBy('createdAt', 'asc')],
    [],
  );
  return useFirestoreCollection<Modification>(
    vehicleId ? `vehicles/${vehicleId}/modifications` : null,
    buildConstraints,
  );
}

// ---------------------------------------------------------------------------
// Write — create
// ---------------------------------------------------------------------------

export interface NewModification {
  title: string;
  category?: Modification['category'];
  description?: string;
  installedAt?: string;
  concoursImpact?: ConcoursImpact;
  reversible?: boolean;
  oeRetained?: boolean;
  judgeVisibility?: JudgeVisibility;
  restorationNote?: string;
  /** Integer minor units (e.g. cents) — never a float. */
  restorationCostMinor?: number;
  currency?: string;
}

export function useCreateModification() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const create = useCallback(async (vehicleId: string, input: NewModification): Promise<string> => {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Not signed in');

    setPending(true);
    setError(null);

    try {
      const colRef = await fsCollection(`vehicles/${vehicleId}/modifications`);
      const docRef = await fsDoc(colRef);
      const now = new Date().toISOString();

      const mod: Modification = {
        id: docRef.id,
        vehicleId,
        title: input.title,
        category: input.category ?? 'other',
        concoursImpact: input.concoursImpact ?? 'none',
        reversible: input.reversible ?? false,
        oeRetained: input.oeRetained ?? false,
        judgeVisibility: input.judgeVisibility ?? 'hidden',
        createdBy: uid,
        createdAt: now,
        ...(input.description ? { description: input.description } : {}),
        ...(input.installedAt ? { installedAt: input.installedAt } : {}),
        ...(input.restorationNote ? { restorationNote: input.restorationNote } : {}),
        ...(typeof input.restorationCostMinor === 'number'
          ? { restorationCostMinor: input.restorationCostMinor }
          : {}),
        ...(input.currency ? { currency: input.currency } : {}),
      };

      // merge:true preserves any future client-only fields added post-creation.
      await fsSetDoc(docRef, mod as unknown as Record<string, unknown>, { merge: true });
      track('modification_logged', {
        category: mod.category,
        reversible: mod.reversible,
        oe_retained: mod.oeRetained,
      });
      return docRef.id;
    } catch (err) {
      // PII: log ids + error codes only, never free-text fields
      const e = err instanceof Error ? err : new Error('Could not create modification');
      setError(e);
      throw e;
    } finally {
      setPending(false);
    }
  }, []);

  return { create, pending, error };
}

// ---------------------------------------------------------------------------
// Write — update
// ---------------------------------------------------------------------------

export interface UpdateModificationInput {
  title?: string;
  category?: Modification['category'];
  description?: string;
  installedAt?: string;
  concoursImpact?: ConcoursImpact;
  reversible?: boolean;
  oeRetained?: boolean;
  judgeVisibility?: JudgeVisibility;
  restorationNote?: string;
  /** Integer minor units (e.g. cents) — never a float. */
  restorationCostMinor?: number;
  currency?: string;
}

export function useUpdateModification() {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const update = useCallback(
    async (vehicleId: string, modId: string, input: UpdateModificationInput): Promise<void> => {
      setPending(true);
      setError(null);

      try {
        const docRef = await fsDoc(`vehicles/${vehicleId}/modifications`, modId);
        const now = new Date().toISOString();

        // Build the update payload — omit undefined fields.
        const payload: Record<string, unknown> = { updatedAt: now };
        if (input.title !== undefined) payload['title'] = input.title;
        if (input.category !== undefined) payload['category'] = input.category;
        if (input.description !== undefined) payload['description'] = input.description;
        if (input.installedAt !== undefined) payload['installedAt'] = input.installedAt;
        if (input.concoursImpact !== undefined) payload['concoursImpact'] = input.concoursImpact;
        if (input.reversible !== undefined) payload['reversible'] = input.reversible;
        if (input.oeRetained !== undefined) payload['oeRetained'] = input.oeRetained;
        if (input.judgeVisibility !== undefined) payload['judgeVisibility'] = input.judgeVisibility;
        if (input.restorationNote !== undefined) payload['restorationNote'] = input.restorationNote;
        if (typeof input.restorationCostMinor === 'number') {
          payload['restorationCostMinor'] = input.restorationCostMinor;
        }
        if (input.currency !== undefined) payload['currency'] = input.currency;

        // merge:true preserves fields not included in this partial update.
        await fsSetDoc(docRef, payload, { merge: true });
      } catch (err) {
        // PII: log ids + error codes only, never free-text fields
        const e = err instanceof Error ? err : new Error('Could not update modification');
        setError(e);
        throw e;
      } finally {
        setPending(false);
      }
    },
    [],
  );

  return { update, pending, error };
}
